import React from 'react';
import MediaAPI, { MediaLibraryItem } from '../../../api/media';
import { RelatedMedia } from './MediaLineage';
import { FolderType, MediaGroup, SourceFilter } from './helpers';
import { LibraryViewMode } from './useAttachmentLibraryState';
import GroupedBrowseTab from './GroupedBrowseTab';
import MediaFilterBar from './MediaFilterBar';
import MediaGrid from './MediaGrid';
import SelectionBar from './SelectionBar';
import MediaInspector from './MediaInspector';
import MediaPagination from './MediaPagination';
import { MediaFilterPreset, MediaPageSize } from './mediaLibraryQuery';
import {
  Density,
  persistDensity,
  readPersistedDensity,
  SortKey,
} from './mediaCardUtils';
import ConfirmModal from '../../modals/ConfirmModal';

// Debounce a value by the given delay in ms.
function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = React.useState<T>(value);
  React.useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

interface BrowseTabProps {
  loading: boolean;
  folderType: FolderType;
  sourceFilter: SourceFilter;
  searchQuery: string;
  folderCounts: Record<FolderType, number>;
  filteredItems: MediaLibraryItem[];
  selectedIds: string[];
  activeItem: MediaLibraryItem | null;
  mode: 'manage' | 'select';
  contextDraft: string;
  aiDescription?: { text: string; model: string; userEdited: boolean };
  descriptionDraft?: string;
  renameDraft: string;
  savingContext: boolean;
  describing?: boolean;
  savingDescription?: boolean;
  renaming: boolean;
  deleting: boolean;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize?: MediaPageSize;
  sortKey?: SortKey;
  hasControlnet?: boolean;
  recentDays?: number | null;
  presets?: MediaFilterPreset[];
  activePresetId?: string | null;
  viewMode: LibraryViewMode;
  groups: MediaGroup[];
  togglingFavouriteId: string | null;
  errorMessage?: string;
  selectedCount?: number;
  onFolderTypeChange: (value: FolderType) => void;
  onSourceFilterChange: (value: SourceFilter) => void;
  onSearchQueryChange: (value: string) => void;
  onSearchSubmit: (query: string) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: MediaPageSize) => void;
  onSortChange?: (sort: SortKey) => void;
  onHasControlnetChange?: (enabled: boolean) => void;
  onRecentDaysChange?: (days: number | null) => void;
  onPresetApply?: (presetId: string) => void;
  onPresetSave?: (name: string) => void;
  onPresetDelete?: (presetId: string) => void;
  onClearFilters?: () => void;
  onFileClick: (mediaId: string) => void;
  onContextDraftChange: (value: string) => void;
  onDescriptionDraftChange?: (value: string) => void;
  onDescribe?: (force: boolean) => void;
  onRenameDraftChange: (value: string) => void;
  onSaveContext: () => void;
  onSaveDescription?: () => void;
  onRename: () => void;
  onRemove: () => void;
  onViewModeChange: (mode: LibraryViewMode) => void;
  onToggleFavourite: (mediaId: string, current: boolean) => void;
  /** Explicit selection toggle (checkbox) — distinct from row-activate. */
  onToggleSelect?: (mediaId: string) => void;
  /** Open the inspector for a media id (manage / grid body click). */
  onInspect?: (mediaId: string) => void;
  /** Range / additive multi-select (cross-page selection). */
  onRangeSelect?: (mediaIds: string[]) => void;
  onAdditiveToggle?: (mediaId: string) => void;
  /** Clear the whole selection. */
  onClearSelection?: () => void;
  onSelectAllMatching?: () => void;
  selectingAll?: boolean;
  allMatchingSelected?: boolean;
  /** Confirm the selection (select mode). */
  onConfirm?: () => void;
  /** Bulk actions (manage mode). */
  onBulkFavourite?: () => void;
  onBulkDelete?: () => void;
  /** Retry a failed load. */
  onRetry?: () => void;
  /** Inspector as a right drawer (page) vs a bottom sheet (modal). */
  inspectorDock?: 'drawer' | 'sheet';
  /** Vertical scroll cap for the grid region (px or CSS length). */
  gridMaxHeight?: string;
  onCropSaved?: (item: MediaLibraryItem) => void | Promise<void>;
  selectedImageCount?: number;
  onAddToDataset?: () => void;
  /** Restore + open the clip an inspected media item belongs to. */
  onRestoreClip?: (clipId: string) => void;
  restoringClip?: boolean;
}

/**
 * The Media Library browse surface — a contact-sheet grid of unified MediaCards
 * with a consolidated sticky filter bar (search + facet chips + density + sort),
 * a persistent cross-page selection / confirm bar, and an on-demand inspector
 * drawer/sheet. The grouped-by-run view is retained as a chip.
 *
 * Public props are unchanged + additive only, so `AttachmentLibraryModal` (and
 * therefore every picker that mounts it as a black box) stays compatible.
 */
const BrowseTab: React.FC<BrowseTabProps> = ({
  loading,
  folderType,
  sourceFilter,
  searchQuery,
  folderCounts,
  filteredItems,
  selectedIds,
  activeItem,
  mode,
  contextDraft,
  aiDescription,
  descriptionDraft,
  renameDraft,
  savingContext,
  describing,
  savingDescription,
  renaming,
  deleting,
  currentPage,
  totalPages,
  totalItems,
  pageSize = 20,
  sortKey = 'newest',
  hasControlnet = false,
  recentDays = null,
  presets = [],
  activePresetId = null,
  viewMode,
  groups,
  togglingFavouriteId,
  errorMessage,
  selectedCount,
  onFolderTypeChange,
  onSourceFilterChange,
  onSearchQueryChange,
  onSearchSubmit,
  onPageChange,
  onPageSizeChange = () => {},
  onSortChange = () => {},
  onHasControlnetChange = () => {},
  onRecentDaysChange = () => {},
  onPresetApply = () => {},
  onPresetSave = () => {},
  onPresetDelete = () => {},
  onClearFilters,
  onFileClick,
  onContextDraftChange,
  onDescriptionDraftChange,
  onDescribe,
  onRenameDraftChange,
  onSaveContext,
  onSaveDescription,
  onRename,
  onRemove,
  onViewModeChange,
  onToggleFavourite,
  onToggleSelect,
  onInspect,
  onRangeSelect,
  onAdditiveToggle,
  onClearSelection,
  onSelectAllMatching,
  selectingAll,
  allMatchingSelected,
  onConfirm,
  onBulkFavourite,
  onBulkDelete,
  onRetry,
  inspectorDock = 'sheet',
  gridMaxHeight = 'min(32rem, 52vh)',
  onCropSaved,
  selectedImageCount = 0,
  onAddToDataset,
  onRestoreClip,
  restoringClip = false,
}) => {
  const activeMediaId = activeItem ? activeItem.media_id || activeItem.id : '';
  const isActiveSelected = activeMediaId ? selectedIds.includes(activeMediaId) : false;

  // Density (persisted, like theme).
  const [density, setDensity] = React.useState<Density>(() => readPersistedDensity());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = React.useState(false);
  const handleDensityChange = React.useCallback((next: Density) => {
    setDensity(next);
    persistDensity(next);
  }, []);

  // Local search input value for debouncing.
  const [localSearch, setLocalSearch] = React.useState(searchQuery);
  const debouncedSearch = useDebounce(localSearch, 250);
  const isSearchActive = localSearch.trim().length > 0;

  // Skip the first run when the debounced value still matches what the parent
  // already loaded — avoids a redundant page-1 refetch on mount.
  const initialSearchRef = React.useRef<string | null>(searchQuery);
  const externalSearchSyncRef = React.useRef(false);

  React.useEffect(() => {
    if (initialSearchRef.current !== null && debouncedSearch === initialSearchRef.current) {
      initialSearchRef.current = null;
      return;
    }
    initialSearchRef.current = null;
    if (externalSearchSyncRef.current && debouncedSearch === searchQuery) {
      externalSearchSyncRef.current = false;
      return;
    }
    onSearchQueryChange(debouncedSearch);
    onSearchSubmit(debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  React.useEffect(() => {
    if (searchQuery === localSearch) return;
    externalSearchSyncRef.current = true;
    setLocalSearch(searchQuery);
    // This effect intentionally reacts only to parent-driven query changes.
    // Including localSearch would classify ordinary typing as an external sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleSearchEnter = React.useCallback(() => {
    onSearchSubmit(localSearch);
  }, [localSearch, onSearchSubmit]);

  // The Store already sorted the complete result set before LIMIT/OFFSET.
  const sortedItems = filteredItems;

  // Inspector open/close. In manage mode the active item drives it; in select
  // mode a dedicated inspect handler opens it.
  const [inspectorId, setInspectorId] = React.useState<string | null>(null);
  // Lineage navigation can target an item that isn't on the current page; hold
  // that snapshot directly so the inspector can render it (and arrow-nav resumes
  // within the page once a page item is opened again).
  const [overrideItem, setOverrideItem] = React.useState<MediaLibraryItem | null>(null);
  const pageInspectorItem =
    mode === 'manage'
      ? activeItem
      : inspectorId
      ? sortedItems.find((it) => (it.media_id || it.id) === inspectorId) || null
      : null;
  // The override wins only while it points at something off the current page;
  // once that id appears on-page (or another card is opened) the page item leads.
  const inspectorItem =
    overrideItem &&
    !sortedItems.some((it) => (it.media_id || it.id) === (overrideItem.media_id || overrideItem.id)) &&
    (!pageInspectorItem ||
      (pageInspectorItem.media_id || pageInspectorItem.id) ===
        (overrideItem.media_id || overrideItem.id))
      ? overrideItem
      : pageInspectorItem;
  const inspectorIndex = inspectorItem
    ? sortedItems.findIndex((it) => (it.media_id || it.id) === (inspectorItem.media_id || inspectorItem.id))
    : -1;
  // The override is an off-page snapshot whose row isn't in the editable parent
  // state — render it read-only (favourite/copy/lineage still work, no editing).
  const inspectorIsOverride = !!inspectorItem && inspectorItem === overrideItem;

  // Lineage (parent / siblings / variations) for the open inspector item. Fetched
  // lazily when the inspector opens / navigates; degrades to no lineage on error.
  const inspectorMediaId = inspectorItem ? inspectorItem.media_id || inspectorItem.id : '';
  const [related, setRelated] = React.useState<RelatedMedia | undefined>(undefined);
  const [relatedLoading, setRelatedLoading] = React.useState(false);
  React.useEffect(() => {
    if (!inspectorMediaId) {
      setRelated(undefined);
      setRelatedLoading(false);
      return;
    }
    let cancelled = false;
    setRelated(undefined);
    setRelatedLoading(true);
    MediaAPI.getRelatedMedia(inspectorMediaId)
      .then((res) => {
        if (cancelled) return;
        setRelated(res);
      })
      .catch(() => {
        if (!cancelled) setRelated({ parent: null, siblings: [], variations: [] });
      })
      .finally(() => {
        if (!cancelled) setRelatedLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [inspectorMediaId]);

  const handleActivate = React.useCallback(
    (mediaId: string) => {
      if (mode === 'select') {
        // Body click toggles the pick (today's behavior).
        onFileClick(mediaId);
      } else {
        // Manage: open the inspector.
        if (onInspect) onInspect(mediaId);
        else onFileClick(mediaId);
      }
    },
    [mode, onFileClick, onInspect]
  );

  const handleInspect = React.useCallback(
    (mediaId: string) => {
      setOverrideItem(null);
      if (mode === 'manage') {
        if (onInspect) onInspect(mediaId);
        else onFileClick(mediaId);
      } else {
        setInspectorId(mediaId);
      }
    },
    [mode, onFileClick, onInspect]
  );

  // Navigate the inspector to a lineage-related item. If it's on the current
  // page, open it normally; otherwise render its snapshot directly (off-page).
  const handleNavigateRelated = React.useCallback(
    (mediaId: string) => {
      const onPage = sortedItems.find((it) => (it.media_id || it.id) === mediaId);
      if (onPage) {
        handleInspect(mediaId);
        return;
      }
      const snapshot =
        (related?.parent && (related.parent.media_id || related.parent.id) === mediaId
          ? related.parent
          : undefined) ||
        related?.siblings.find((it) => (it.media_id || it.id) === mediaId) ||
        related?.variations.find((it) => (it.media_id || it.id) === mediaId) ||
        null;
      if (snapshot) {
        if (mode === 'manage' && onInspect) onInspect('');
        setInspectorId(null);
        setOverrideItem(snapshot);
      }
    },
    [sortedItems, related, handleInspect, mode, onInspect]
  );

  const handleToggleSelect = React.useCallback(
    (mediaId: string) => {
      if (onToggleSelect) onToggleSelect(mediaId);
      else onFileClick(mediaId);
    },
    [onToggleSelect, onFileClick]
  );

  const navInspector = React.useCallback(
    (delta: number) => {
      if (inspectorIndex < 0) return;
      const nextIndex = inspectorIndex + delta;
      if (nextIndex < 0 || nextIndex >= sortedItems.length) return;
      const next = sortedItems[nextIndex];
      const nextId = next.media_id || next.id;
      setOverrideItem(null);
      if (mode === 'manage') {
        if (onInspect) onInspect(nextId);
        else onFileClick(nextId);
      } else {
        setInspectorId(nextId);
      }
    },
    [inspectorIndex, sortedItems, mode, onInspect, onFileClick]
  );

  const closeInspector = React.useCallback(() => {
    setInspectorId(null);
    setOverrideItem(null);
    if (mode === 'manage' && onInspect) onInspect('');
  }, [mode, onInspect]);

  const count = typeof selectedCount === 'number' ? selectedCount : selectedIds.length;

  return (
    <div className="relative space-y-3">
      <MediaFilterBar
        searchValue={localSearch}
        onSearchChange={setLocalSearch}
        onSearchEnter={handleSearchEnter}
        isSearching={isSearchActive}
        viewMode={viewMode}
        onViewModeChange={onViewModeChange}
        folderType={folderType}
        onFolderTypeChange={onFolderTypeChange}
        folderCounts={folderCounts}
        sourceFilter={sourceFilter}
        onSourceFilterChange={onSourceFilterChange}
        density={density}
        onDensityChange={handleDensityChange}
        sortKey={sortKey}
        onSortChange={onSortChange}
        hasControlnet={hasControlnet}
        onHasControlnetChange={onHasControlnetChange}
        recentDays={recentDays}
        onRecentDaysChange={onRecentDaysChange}
        presets={presets}
        activePresetId={activePresetId}
        onPresetApply={onPresetApply}
        onPresetSave={onPresetSave}
        onPresetDelete={onPresetDelete}
      />

      {viewMode === 'grouped' ? (
        <div className="overflow-auto" style={{ maxHeight: gridMaxHeight }}>
          <GroupedBrowseTab
            groups={groups}
            activeItem={activeItem}
            togglingFavouriteId={togglingFavouriteId}
            mode={mode}
            onFileClick={mode === 'select' ? onFileClick : handleInspect}
            onToggleFavourite={onToggleFavourite}
            onToggleSelect={mode === 'select' ? onFileClick : onToggleSelect}
            selectedIds={selectedIds}
          />
        </div>
      ) : (
        <div className="overflow-auto" style={{ maxHeight: gridMaxHeight }}>
          <MediaGrid
            items={sortedItems}
            selectedIds={selectedIds}
            selectMode={mode === 'select'}
            density={density}
            loading={loading}
            togglingFavouriteId={togglingFavouriteId}
            hasActiveFilters={isSearchActive || folderType !== 'all' || sourceFilter !== 'all' || viewMode !== 'all'}
            errorMessage={errorMessage}
            endLabel={
              totalPages <= 1 && sortedItems.length > 0
                ? `· END · ${totalItems || sortedItems.length} items ·`
                : undefined
            }
            onActivate={mode === 'select' ? handleActivate : handleInspect}
            onToggleSelect={mode === 'select' ? handleActivate : handleToggleSelect}
            onToggleFavourite={onToggleFavourite}
            onRangeSelect={mode === 'select' ? onRangeSelect : undefined}
            onAdditiveToggle={mode === 'select' ? onAdditiveToggle : undefined}
            onClearFilters={() => {
              setLocalSearch('');
              if (onClearFilters) onClearFilters();
              else {
                onViewModeChange('all');
                onFolderTypeChange('all');
                onSourceFilterChange('all');
              }
            }}
            onRetry={onRetry}
          />
        </div>
      )}

      <MediaPagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        loading={loading}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />

      {/* Persistent selection / confirm bar — surfaces cross-page selection. */}
      <SelectionBar
        count={count}
        mode={mode}
        totalMatching={totalItems}
        onClear={() => onClearSelection?.()}
        onSelectAll={onSelectAllMatching}
        selectingAll={selectingAll}
        allMatchingSelected={allMatchingSelected}
        onConfirm={onConfirm}
        onBulkFavourite={onBulkFavourite}
        onBulkDelete={onBulkDelete ? () => setShowBulkDeleteConfirm(true) : undefined}
        busy={deleting}
        selectedImageCount={selectedImageCount}
        onAddToDataset={onAddToDataset}
      />

      {/* Inspector drawer / bottom sheet. */}
      {inspectorItem && (
        <MediaInspector
          item={inspectorItem}
          mode={mode}
          asSheet={inspectorDock === 'sheet'}
          index={inspectorIndex < 0 ? 0 : inspectorIndex}
          total={sortedItems.length}
          onPrev={() => navInspector(-1)}
          onNext={() => navInspector(1)}
          onClose={closeInspector}
          contextDraft={contextDraft}
          aiDescription={aiDescription}
          descriptionDraft={descriptionDraft}
          renameDraft={renameDraft}
          savingContext={savingContext}
          describing={describing}
          savingDescription={savingDescription}
          renaming={renaming}
          deleting={deleting}
          onContextDraftChange={onContextDraftChange}
          onDescriptionDraftChange={onDescriptionDraftChange}
          onDescribe={onDescribe}
          onRenameDraftChange={onRenameDraftChange}
          onSaveContext={onSaveContext}
          onSaveDescription={onSaveDescription}
          onRename={onRename}
          onRemove={onRemove}
          isSelected={isActiveSelected || (inspectorId ? selectedIds.includes(inspectorId) : false)}
          onToggleSelect={mode === 'select' ? handleToggleSelect : undefined}
          onToggleFavourite={onToggleFavourite}
          togglingFavourite={
            togglingFavouriteId === (inspectorItem.media_id || inspectorItem.id)
          }
          related={related}
          relatedLoading={relatedLoading}
          onNavigateRelated={handleNavigateRelated}
          readOnly={inspectorIsOverride}
          onCropSaved={onCropSaved}
          onRestoreClip={onRestoreClip}
          restoringClip={restoringClip}
        />
      )}
      <ConfirmModal
        isOpen={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={() => onBulkDelete?.()}
        title={`Delete ${count} selected file${count === 1 ? '' : 's'}?`}
        message="This permanently removes the stored originals and thumbnails for every selected item. This cannot be undone."
        confirmText={`Delete ${count}`}
        variant="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default BrowseTab;
